import React from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { IDictionary } from 'models/dictionary';
import { IAmmunition, IAncillary } from 'models/ancillaries';
import { parseDictionary } from 'helpers/dictionary';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';

interface IAncillaryDataListItem {
  data: IAmmunition | IAncillary;
  state: IDictionary[] | null;
}

const AmmunitionDataListItem = ({ data, state }: IAncillaryDataListItem) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const isLegal = data.legalityUniqueId === LEGAL_ID;

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: `${isLegal ? colors.gray100 : colors.error15}`,
      }}
    >
      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.firecycleId.label')}
        </Typography>
        <Typography variant="body2">{data.fireCycleId}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.lotNumber.label')}
        </Typography>
        <Typography variant="body2">{data.lotNumber || '-'}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.batchNumber.label')}
        </Typography>
        <Typography variant="body2">{data.batchNumber || '-'}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.ammunitionType.label')}
        </Typography>
        <Typography variant="body2">{data.type || '-'}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.calibre.label')}
        </Typography>
        <Typography variant="body2">{data.calibre || '-'}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'ancillaries.ancillariesLocation.ammunitionDetails.section.ammunitionManufacturer.label',
          )}
        </Typography>
        <Typography variant="body2">{data.manufacturer || '-'}</Typography>
      </Grid>

      <Grid item xs={12} md={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.state.label')}
        </Typography>
        <Chips
          text={parseDictionary(state, 'name')[data.stateUniqueId]}
          variant={
            renderDataColorByValues(
              data.activityUniqueId,
              data.legalityUniqueId,
              data.stateUniqueId,
            ).stateField
          }
        />
      </Grid>

      <Grid
        container
        sx={{
          margin: '0 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.gray200}`,
          },
        }}
        p={2}
      >
        <Grid item xs={12} sm={12}>
          <Button
            label={t('registeredFirearms.firearmsLocation.seeDetails.label')}
            variant={EButtonVariants.outlined}
            onClick={() =>
              navigate(`${ERouteLinks.ViewAmmunition.replace(':id', data.uniqueId)}`)
            }
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AmmunitionDataListItem;
